import {
  addOrbitRemoteStrategy,
  updateOrbitRemoteStrategy,
  startOrbitRemoteStrategy,
  stopOrbitRemoteStrategy,
  deleteOrbitRemoteStrategy,
  getOrbitRemoteStrategyDetail,
  getOrbitRemoteStrategyPositions,
  getOrbitRemoteStrategyOrders
} from '@/server/orbit'

// Orbit is an independent OKX strategy. Do not reuse futures_x/hedge parameters.
export const ORBIT_LIMITS = Object.freeze({ minimum: 1000, maximum: 10000, leverage: 10 })
export const ORBIT_I18N_KEYS = Object.freeze({
  amountInvalid: 'robot.Please_enter_position_amount',
  amountBelowMinimum: 'robot.orbit_amount_below_minimum',
  amountAboveMaximum: 'robot.orbit_amount_above_maximum',
  balanceRequired: 'robot.orbit_balance_required',
  balanceExceeded: 'robot.Set_funds_cannot_exceed_balance',
  metadataMissing: 'robot.orbit_metadata_missing',
  createFailed: 'robot.orbit_create_failed',
  operationFailed: 'robot.orbit_operation_failed',
  historyReadonly: 'robot.orbit_history_readonly',
  dataLoadFailed: 'robot.orbit_data_load_failed'
})
export const ORBIT_CURSOR_ERRORS = [
  'remote_strategy_order_snapshot_changed',
  'remote_strategy_invalid_cursor',
  'remote_strategy_order_snapshot_stale'
]

export function orbitNumber(value) {
  if (value === undefined || value === null || String(value).trim() === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export function orbitAmountIssue(value, { mode = 'create', balance, balanceLoaded } = {}) {
  const amount = orbitNumber(value)
  if (amount === null) return ORBIT_I18N_KEYS.amountInvalid
  if (amount < ORBIT_LIMITS.minimum) return ORBIT_I18N_KEYS.amountBelowMinimum
  if (amount > ORBIT_LIMITS.maximum) return ORBIT_I18N_KEYS.amountAboveMaximum
  if (mode !== 'modify') {
    if (!balanceLoaded || orbitNumber(balance) === null) return ORBIT_I18N_KEYS.balanceRequired
    if (amount > orbitNumber(balance)) return ORBIT_I18N_KEYS.balanceExceeded
  }
  return ''
}

function orbitError(message, code) {
  const error = new Error(message)
  error.code = code || ''
  return error
}

function orbitI18nError(key, code) {
  const error = orbitError(key, code)
  error.i18nKey = key
  return error
}

export function assertOrbitSuccess(response, fallback = ORBIT_I18N_KEYS.operationFailed) {
  if (!response || !response.success || String(response.statusCode) === '40101') {
    if (response && response.message) throw orbitError(response.message, response.statusCode)
    throw orbitI18nError(fallback, response && response.statusCode)
  }
  return response.data
}

export function orbitIdentity(raw = {}) {
  if (raw.sid === undefined || raw.sid === null || String(raw.sid).trim() === '') {
    throw orbitI18nError(ORBIT_I18N_KEYS.metadataMissing, 'orbit_metadata_missing')
  }
  return { robotType: '1', sid: raw.sid }
}

export function orbitActionParams(raw = {}) {
  const identity = orbitIdentity(raw)
  if (raw.version === undefined || raw.version === null || String(raw.version).trim() === '') {
    throw orbitI18nError(ORBIT_I18N_KEYS.metadataMissing, 'orbit_metadata_missing')
  }
  return { ...identity, version: String(raw.version) }
}

export async function createOrbitStrategy(payload) {
  const issue = orbitAmountIssue(payload.amount, { ...payload, mode: 'create' })
  if (issue) throw orbitI18nError(issue, issue)
  const response = await addOrbitRemoteStrategy({
    robotType: '1',
    space: String(orbitNumber(payload.amount)),
    leverage: String(ORBIT_LIMITS.leverage),
    stopLoss: '0',
    lossTransferRate: '70',
    state: '1',
    strategyParams: JSON.stringify({ riskLevel: 'balanced' })
  })
  assertOrbitSuccess(response, ORBIT_I18N_KEYS.createFailed)
  return response
}

export async function executeOrbitStrategyAction({ action, robot, form = {} }) {
  const raw = robot && robot.raw || {}
  if (raw.__historyStrategy) throw orbitI18nError(ORBIT_I18N_KEYS.historyReadonly)
  const params = orbitActionParams(raw)
  let request
  if (action === 'modify') {
    const issue = orbitAmountIssue(form.amount, { mode: 'modify' })
    if (issue) throw orbitI18nError(issue, issue)
    params.space = String(orbitNumber(form.amount))
    params.leverage = String(raw.leverage || ORBIT_LIMITS.leverage)
    request = updateOrbitRemoteStrategy
  } else {
    request = { pause: stopOrbitRemoteStrategy, resume: startOrbitRemoteStrategy, delete: deleteOrbitRemoteStrategy }[action]
  }
  if (!request) throw orbitI18nError(ORBIT_I18N_KEYS.operationFailed)
  const response = await request(params)
  assertOrbitSuccess(response)
  return response
}

export function mergeOrbitDetail(raw = {}, data = {}) {
  const available = {}
  const profitFields = ['profit_total', 'profit_rate', 'profit_rate_year', 'profit_times']
  Object.keys(data || {}).forEach(key => {
    if (profitFields.includes(key) || (data[key] !== undefined && data[key] !== null && data[key] !== '')) {
      available[key] = data[key]
    }
  })
  return {
    ...raw,
    ...available,
    product_type: 'orbit',
    robot_type: raw.robot_type === undefined ? 'ok' : raw.robot_type,
    __robotType: 1,
    coin: 'USDT',
    coin_one: 'USDT',
    future_type: 1,
    add_time: available.create_time || available.add_time || raw.add_time,
    stop_loss: available.stop_loss === undefined ? (available.stopLoss === undefined ? raw.stop_loss : available.stopLoss) : available.stop_loss,
    loss_transfer_rate: available.loss_transfer_rate === undefined ? (available.lossTransferRate === undefined ? raw.loss_transfer_rate : available.lossTransferRate) : available.loss_transfer_rate
  }
}

export function normalizeOrbitPositions(payload = {}) {
  const summary = payload.summary || {}
  const list = Array.isArray(payload.positions) ? payload.positions : []
  const rows = list.map(item => ({
    ...item,
    symbol: item.instrument_id || '',
    side: item.direction === 'long' ? 'buy' : item.direction === 'short' ? 'sell' : '',
    total_size: item.position_size,
    profit: item.unrealized_pnl_usdt,
    profit_rate: item.unrealized_pnl_percent,
    margin_ratio: summary.margin_ratio,
    avg_price: item.average_open_price,
    force_price: item.liquidation_price,
    initial_margin: item.initial_margin_usdt,
    mmr: item.maintenance_margin_usdt
  }))
  const total = orbitNumber(summary.filtered_position_count)
  return { rows, summary, total: total === null ? rows.length : total }
}

export function normalizeOrbitOrders(payload = {}) {
  const list = Array.isArray(payload.orders) ? payload.orders : []
  const rows = list.map(item => ({
    ...item,
    symbol: item.instrument_id || item.symbol || '',
    type: item.reduce_only === true || item.reduce_only === 1 || item.reduce_only === 'true' || item.reduce_only === '1' ? 'close' : 'open',
    size: item.requested_size,
    filledSize: item.filled_size,
    averagePrice: item.average_fill_price,
    entrustTime: String(item.submitted_at || item.updated_at || '').replace('T', ' ').replace(/Z$/, '').slice(0, 19)
  }))
  const total = orbitNumber(payload.summary && payload.summary.filtered_open_order_count)
  return { rows, total: total === null ? rows.length : total, nextCursor: payload.next_cursor || '' }
}

export async function loadOrbitStrategyDetail(robot, options = {}) {
  const raw = robot && robot.raw || {}
  const params = orbitIdentity(raw)
  const only = options.only || 'all'
  const result = { detailKind: 'orbit', errors: [], sectionErrors: {} }
  const tasks = []
  const run = (section, request, apply) => {
    if (only !== 'all' && only !== section) return
    tasks.push((async () => {
      try {
        const data = assertOrbitSuccess(await request(), ORBIT_I18N_KEYS.dataLoadFailed) || {}
        apply(data)
      } catch (error) {
        result.errors.push(error.message)
        result.sectionErrors[section] = { message: error.message, code: error.code || '', i18nKey: error.i18nKey || '' }
      }
    })())
  }
  run('detail', () => getOrbitRemoteStrategyDetail(params), data => { result.detail = mergeOrbitDetail(raw, data) })
  run('positions', () => getOrbitRemoteStrategyPositions({ ...params, positionSide: 'all' }), data => {
    const page = normalizeOrbitPositions(data)
    result.positions = page.rows
    result.positionTotal = page.total
    result.positionSummary = page.summary
  })
  run('orders', () => getOrbitRemoteStrategyOrders({
    ...params,
    side: 'all',
    pageSize: options.pageSize || 10,
    ...(options.cursor ? { cursor: options.cursor } : {})
  }), data => {
    const page = normalizeOrbitOrders(data)
    result.orders = page.rows
    result.orderTotal = page.total
    result.orderNextCursor = page.nextCursor
  })
  await Promise.all(tasks)
  return result
}
